import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Users, DollarSign, Briefcase, Settings, Lock, Cloud } from 'lucide-react';

interface AdminRHRACIModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdminRHRACIModal: React.FC<AdminRHRACIModalProps> = ({ isOpen, onClose }) => {
  const [ongletActif, setOngletActif] = useState<'gouvernance' | 'finance' | 'operations' | 'rh' | 'outils' | 'licences'>('gouvernance');

  if (!isOpen) return null;

  // Acteurs avec avatars
  const acteurs = [
    { id: 'francois', nom: 'François Delisle', role: 'VP Gouvernance corporative', avatar: '/FP.png' },
    { id: 'melissa', nom: 'Mélissa', role: 'Cheffe spécialiste comptabilité BZ', avatar: '/MP.png' },
    { id: 'lah', nom: 'LAH', role: 'Directeur RH & opérations administratives', avatar: '/LAH.jpg' },
    { id: 'richard', nom: 'Richard Delisle', role: 'Directeur Cloud & Licences', avatar: '/RD.png' },
    { id: 'yannick', nom: 'Dany', role: 'Directeur RH', avatar: '/DB.png' },
    { id: 'dany', nom: 'Dany', role: 'Amélioration continue & innovation', avatar: '/DB.png' }
  ];

  // Matrice RACI - Gouvernance & Conformité
  const raciGouvernance = [
    { domaine: 'Cadres & politiques', francois: 'A', melissa: 'I', lah: 'I', richard: 'I', yannick: 'I', dany: 'C' },
    { domaine: 'Conformité globale', francois: 'A', melissa: 'R', lah: 'I', richard: 'I', yannick: 'I', dany: 'C' },
    { domaine: 'Gestion des exceptions', francois: 'A', melissa: 'R', lah: 'C', richard: 'I', yannick: 'I', dany: 'I' },
    { domaine: 'Respect des procédures', francois: 'C', melissa: 'A / R', lah: 'I', richard: 'I', yannick: 'I', dany: 'I' },
    { domaine: 'Audits internes', francois: 'A', melissa: 'R', lah: 'C', richard: 'I', yannick: 'I', dany: 'I' }
  ];

  // Matrice RACI - Finance & Comptabilité
  const raciFinance = [
    { domaine: 'Fermeture de mois', francois: 'I', melissa: 'A / R', lah: 'I', richard: 'I', yannick: 'I', dany: 'I' },
    { domaine: 'Paie', francois: 'I', melissa: 'A / R', lah: 'I', richard: 'I', yannick: 'I', dany: 'I' },
    { domaine: 'États financiers', francois: 'I', melissa: 'A', lah: 'I', richard: 'I', yannick: 'I', dany: 'C' },
    { domaine: 'Amortissements', francois: 'I', melissa: 'A / R', lah: 'I', richard: 'I', yannick: 'I', dany: 'C' },
    { domaine: 'Discipline financière', francois: 'C', melissa: 'A', lah: 'R', richard: 'I', yannick: 'I', dany: 'I' }
  ];

  // Matrice RACI - Opérations Administratives & Recevables
  const raciOperations = [
    { domaine: 'Recevables', francois: 'I', melissa: 'C', lah: 'A / R', richard: 'I', yannick: 'I', dany: 'I' },
    { domaine: 'Flux admin quotidiens', francois: 'I', melissa: 'I', lah: 'A / R', richard: 'I', yannick: 'I', dany: 'I' },
    { domaine: 'Application des procédures', francois: 'C', melissa: 'I', lah: 'A / R', richard: 'I', yannick: 'I', dany: 'I' },
    { domaine: 'Documentation opérationnelle', francois: 'C', melissa: 'I', lah: 'A / R', richard: 'I', yannick: 'I', dany: 'I' }
  ];

  // Matrice RACI - RH & Dossiers Employés
  const raciRH = [
    { domaine: 'Cadres RH & conformité', francois: 'I', melissa: 'R', lah: 'R', richard: '-', yannick: 'A', dany: 'C' },
    { domaine: 'Onboarding', francois: 'I', melissa: 'R', lah: 'R', richard: '-', yannick: 'A', dany: 'I' },
    { domaine: 'Dossier employé', francois: 'I', melissa: 'R', lah: 'R', richard: '-', yannick: 'A', dany: 'I' },
    { domaine: 'Plaintes & incidents', francois: 'I', melissa: 'R', lah: 'R', richard: '-', yannick: 'C', dany: 'A' },
    { domaine: 'PRIsME Employé', francois: 'I', melissa: 'I', lah: 'I', richard: '-', yannick: 'I', dany: 'A / R' }
  ];

  // Matrice RACI - Licences & Contrats
  const raciLicences = [
    { domaine: 'Gestion des licences Microsoft 365', francois: 'I', melissa: 'I', lah: 'R', richard: 'A', yannick: 'I', dany: 'I' },
    { domaine: 'Contrôle des licences', francois: 'I', melissa: 'I', lah: 'R', richard: 'A', yannick: 'I', dany: 'I' },
    { domaine: 'Rapports quotidiens licences', francois: 'I', melissa: 'I', lah: 'R', richard: 'A', yannick: 'I', dany: 'I' },
    { domaine: 'Validation rapports licences', francois: 'I', melissa: 'I', lah: 'R', richard: 'A', yannick: 'I', dany: 'I' },
    { domaine: 'Logistique licences', francois: 'I', melissa: 'I', lah: 'R', richard: 'A', yannick: 'I', dany: 'I' },
    { domaine: 'Gestion des contrats', francois: 'I', melissa: 'I', lah: 'R', richard: 'A', yannick: 'I', dany: 'I' },
    { domaine: 'Contrôle des contrats', francois: 'I', melissa: 'I', lah: 'R', richard: 'A', yannick: 'I', dany: 'I' },
    { domaine: 'Rapports contrats', francois: 'I', melissa: 'I', lah: 'R', richard: 'A', yannick: 'I', dany: 'I' }
  ];

  // Matrice RACI - Outils & Systèmes
  const raciOutils = [
    { domaine: 'Cadres d\'utilisation', francois: 'A', melissa: 'R', lah: 'R', richard: 'I', yannick: 'I', dany: 'C' },
    { domaine: 'Adoption des outils', francois: 'C', melissa: 'R', lah: 'A / R', richard: 'I', yannick: 'I', dany: 'C' },
    { domaine: 'Évolution des outils', francois: 'I', melissa: 'C', lah: 'C', richard: 'I', yannick: 'I', dany: 'A / R' },
    { domaine: 'Innovation & AI', francois: 'I', melissa: 'C', lah: 'C', richard: 'I', yannick: 'I', dany: 'A / R' },
    { domaine: 'Centralisation des données', francois: 'C', melissa: 'R', lah: 'A / R', richard: 'I', yannick: 'I', dany: 'C' }
  ];

  const getRACIColor = (role: string) => {
    if (role.includes('A')) return 'bg-red-100 text-red-800 border-red-300';
    if (role.includes('R')) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (role.includes('C')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    if (role.includes('I')) return 'bg-green-100 text-green-800 border-green-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const renderRACITable = (data: any[], acteurs: any[]) => {
    return (
      <div className="overflow-x-auto -mx-8 px-8">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gradient-to-r from-teal-600 to-emerald-600 text-white">
              <th className="px-3 py-2 text-left font-bold border border-white/20 text-xs">Domaine</th>
              {acteurs.map(acteur => (
                <th key={acteur.id} className="px-2 py-2 text-center font-bold border border-white/20">
                  <div className="flex flex-col items-center gap-0.5">
                    {acteur.avatar && (
                      <img src={acteur.avatar} alt={acteur.nom} className="w-6 h-6 rounded-full" />
                    )}
                    <span className="text-xs leading-tight">{acteur.nom.split(' ')[0]}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-3 py-2 font-semibold text-gray-900 border border-gray-200 text-xs">
                  {row.domaine}
                </td>
                {acteurs.map(acteur => {
                  const role = row[acteur.id];
                  return (
                    <td key={acteur.id} className="px-2 py-2 text-center border border-gray-200">
                      {role && role !== '-' ? (
                        <span className={`inline-block px-2 py-0.5 rounded font-bold text-xs border ${getRACIColor(role)}`}>
                          {role}
                        </span>
                      ) : (
                        <span className="text-gray-300 text-xs">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-[95vw] h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-teal-600 via-emerald-600 to-green-600 p-6">
            <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors backdrop-blur-sm cursor-pointer"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <div className="relative flex items-center gap-4">
              <div className="bg-white/20 backdrop-blur-xl p-4 rounded-2xl">
                <Lock className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-black text-white mb-1">
                  🔐 RACI officiel – Administration & RH
                </h1>
                <p className="text-white/90 text-lg">
                  Référence unique pour les rôles et responsabilités en gouvernance, finance, RH, opérations administratives et outils
                </p>
              </div>
            </div>
          </div>

          {/* Contenu */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Principes RACI - Version compacte */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-b border-gray-200 px-6 py-3">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-semibold text-gray-700">Principes RACI :</span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-bold border border-red-300">R</span>
                      <span className="text-xs text-gray-600">Responsible</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-bold border border-red-500">A</span>
                      <span className="text-xs text-gray-600">Accountable</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs font-bold border border-yellow-300">C</span>
                      <span className="text-xs text-gray-600">Consulted</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs font-bold border border-green-300">I</span>
                      <span className="text-xs text-gray-600">Informed</span>
                    </div>
                  </div>
                </div>
                <div className="bg-red-50 border border-red-300 rounded px-3 py-1">
                  <p className="text-red-800 font-bold text-xs flex items-center gap-1.5">
                    <Shield className="w-3 h-3" />
                    ⚠️ Un seul A par sujet
                  </p>
                </div>
              </div>
            </div>

            {/* Acteurs - Version compacte */}
            <div className="bg-gray-50 border-b border-gray-200 px-6 py-3">
              <div className="flex items-center gap-4">
                <Users className="w-5 h-5 text-teal-600" />
                <span className="text-sm font-semibold text-gray-700">Acteurs :</span>
                <div className="flex items-center gap-3 flex-wrap">
                  {acteurs.map(acteur => (
                    <div key={acteur.id} className="flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 border border-gray-200">
                      {acteur.avatar && (
                        <img src={acteur.avatar} alt={acteur.nom} className="w-6 h-6 rounded-full" />
                      )}
                      <div>
                        <div className="font-semibold text-xs text-gray-900">{acteur.nom}</div>
                        <div className="text-xs text-gray-500">{acteur.role}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Onglets */}
            <div className="border-b border-gray-200 bg-white px-6 py-2">
              <div className="flex gap-1 overflow-x-auto">
                {[
                  { id: 'gouvernance', label: 'Gouvernance & Conformité', icon: Shield },
                  { id: 'finance', label: 'Finance & Comptabilité', icon: DollarSign },
                  { id: 'operations', label: 'Opérations Admin & Recevables', icon: Briefcase },
                  { id: 'rh', label: 'RH & Dossiers Employés', icon: Users },
                  { id: 'licences', label: 'Licences & Contrats', icon: Cloud },
                  { id: 'outils', label: 'Outils & Systèmes', icon: Settings }
                ].map((onglet) => {
                  const Icon = onglet.icon;
                  return (
                    <button
                      key={onglet.id}
                      onClick={() => setOngletActif(onglet.id as any)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-t-lg transition-all text-sm font-medium whitespace-nowrap ${
                        ongletActif === onglet.id
                          ? 'bg-teal-600 text-white shadow-lg'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {onglet.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Contenu des onglets */}
            <div className="flex-1 overflow-y-auto p-6">
              {ongletActif === 'gouvernance' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-teal-600" />
                    RACI – Gouvernance & Conformité
                  </h3>
                  {renderRACITable(raciGouvernance, acteurs)}
                </div>
              )}

              {ongletActif === 'finance' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-teal-600" />
                    RACI – Finance & Comptabilité
                  </h3>
                  {renderRACITable(raciFinance, acteurs)}
                </div>
              )}

              {ongletActif === 'operations' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-teal-600" />
                    RACI – Opérations Administratives & Recevables
                  </h3>
                  {renderRACITable(raciOperations, acteurs)}
                </div>
              )}

              {ongletActif === 'rh' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="w-5 h-5 text-teal-600" />
                    RACI – RH & Dossiers Employés
                  </h3>
                  {renderRACITable(raciRH, acteurs)}
                </div>
              )}

              {ongletActif === 'licences' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Cloud className="w-5 h-5 text-teal-600" />
                    RACI – Licences & Contrats
                  </h3>
                  <div className="bg-blue-50 border border-blue-300 rounded-lg p-3 mb-4">
                    <p className="text-blue-900 font-semibold text-xs mb-1">📋 Contexte : Revendeur Microsoft 365</p>
                    <p className="text-blue-800 text-xs leading-relaxed">
                      BZ Inc. est revendeur de produits Microsoft 365 et autres. La logistique est importante avec des rapports quotidiens à sortir et valider.
                      Richard Delisle (Directeur Cloud & Licences) est l'imputable (Accountable) pour contrôler la gestion des licences et contrats.
                      LAH gère les opérations administratives et rend le tout vivant (Responsible), en coordination avec Richard pour les licences et François pour la gouvernance.
                    </p>
                  </div>
                  {renderRACITable(raciLicences, acteurs)}
                </div>
              )}

              {ongletActif === 'outils' && (
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <Settings className="w-5 h-5 text-teal-600" />
                    RACI – Outils & Systèmes
                  </h3>
                  {renderRACITable(raciOutils, acteurs)}
                </div>
              )}
            </div>

            {/* Règle d'or et Note - Version compacte */}
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-2">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-red-600" />
                  <p className="text-red-800 font-semibold text-xs">
                    Règle d'Or : Si ce n'est pas clair qui est Accountable, ce n'est pas gouverné.
                  </p>
                </div>
                <p className="text-xs text-blue-900 text-right">
                  <strong>Note :</strong> Ce RACI est <strong>vivant</strong> et <strong>révisable</strong>, 
                  mais <strong>officiel</strong> tant qu'il n'est pas modifié dans PRIsME.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default AdminRHRACIModal;
